import { setSidebarOpenTab } from '@/atoms/persistedPreferencesAtoms';
import { chatHistoryOverlayOpenAtom, showSignUpModalAtom } from '@/atoms/chatAtoms';
import { chatSidebarOpenAtom } from '@/atoms/chatSidebarAtoms';
import { usePaywallStates } from '@/atoms/paywallAtoms';
import { userDocumentsModalOpenAtom } from '@heidi/evidence';
import { isMobileHeaderSheetOpenAtom } from '@/components/layout/header/mobile-app-header/mobile-header-sheet-atoms';
import { useResetProjectsComposerContext } from '@heidi/agents/data';
import { getBaseChatPath } from '@heidi/agents/contracts';
import { useEvidenceBillingInfo } from '@/hooks/useEvidenceBillingInfo';
import { askHeidiClosed } from '@/lib/analytics/scribe/ask-ai';
import { useAtom, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { type ChatMode } from '@heidi/evidence/contracts';

interface UseChatHeaderActionsProps {
  onClose: () => void;
  mode: ChatMode;
  onOpenPanel?: () => void;
  /**
   * Whether this user's chat home is Work rather than Evidence. A resolved
   * boolean: the header already resolves flags, and `getBaseChatPath` turns
   * that into a path.
   */
  canUseWorkHome: boolean;
}

export const useChatHeaderActions = ({
  onClose,
  mode,
  canUseWorkHome,
  onOpenPanel,
}: UseChatHeaderActionsProps) => {
  const router = useRouter();
  const setIsMobileHeaderSheetOpen = useSetAtom(isMobileHeaderSheetOpenAtom);
  const setHistoryOverlayOpen = useSetAtom(chatHistoryOverlayOpenAtom);
  const [isSidebarOpen, setSidebarOpen] = useAtom(chatSidebarOpenAtom);
  const [userDocsModalOpen, setUserDocsModalOpen] = useAtom(userDocumentsModalOpenAtom);
  const [showSignUpModal, setShowSignUpModal] = useAtom(showSignUpModalAtom);
  const { hasEvidencePro } = useEvidenceBillingInfo();
  const { toggleUpgradeEvidenceProModal } = usePaywallStates();
  // Flag-aware base home for the in-app ``/scribe/chat`` surface (Work when
  // Projects is on, Evidence otherwise). Embed/public ``/chat`` surfaces are
  // not projects-aware and keep their own literals below.
  const baseChatHref = getBaseChatPath(canUseWorkHome);
  const resetProjectsComposerContext = useResetProjectsComposerContext();
  const {
    trackNewChatClicked,
    trackDeepLinkSignInClicked,
    trackDeepLinkSignUpClicked,
    trackSidebarOpen,
  } = useEvidenceAnalytics();

  const isOnSessionRoute = router.pathname.startsWith('/scribe/session/');

  const handleClose = useCallback(() => {
    if (!isOnSessionRoute) {
      setSidebarOpenTab(undefined);
    }
    onClose();
    askHeidiClosed(mode);
  }, [onClose, mode, isOnSessionRoute]);

  const handleMenuClick = useCallback(
    () => setIsMobileHeaderSheetOpen(true),
    [setIsMobileHeaderSheetOpen]
  );

  const handleNewChat = useCallback(() => {
    trackNewChatClicked('header');
    const basePath = router.pathname.includes('/scribe/chat')
      ? baseChatHref
      : router.pathname.includes('/chat/embed')
        ? '/chat/embed'
        : '/chat';
    resetProjectsComposerContext();
    void router.push(basePath, undefined, { shallow: true });
  }, [router, trackNewChatClicked, baseChatHref, resetProjectsComposerContext]);

  const handleSignInClick = useCallback(() => {
    trackDeepLinkSignInClicked();
    setShowSignUpModal(true);
  }, [trackDeepLinkSignInClicked]);

  const handleSignUpClick = useCallback(() => {
    trackDeepLinkSignUpClicked();
    setShowSignUpModal(true);
  }, [trackDeepLinkSignUpClicked]);

  const handlePanelClick = useCallback(() => {
    if (onOpenPanel) {
      onOpenPanel();
    } else {
      setSidebarOpen(true);
    }
    trackSidebarOpen();
  }, [onOpenPanel, setSidebarOpen, trackSidebarOpen]);

  const handleHistoryClick = useCallback(
    () => setHistoryOverlayOpen(true),
    [setHistoryOverlayOpen]
  );

  const handleCustomizeEvidenceClick = useCallback(
    () =>
      hasEvidencePro ? setUserDocsModalOpen(true) : toggleUpgradeEvidenceProModal(true),
    [hasEvidencePro, setUserDocsModalOpen, toggleUpgradeEvidenceProModal]
  );

  return {
    isSidebarOpen,
    userDocsModalOpen,
    setUserDocsModalOpen,
    showSignUpModal,
    setShowSignUpModal,
    hasEvidencePro,
    handleClose,
    handleMenuClick,
    handleNewChat,
    handleSignInClick,
    handleSignUpClick,
    handlePanelClick,
    handleHistoryClick,
    handleCustomizeEvidenceClick,
  };
};
